import api from '../../api/flights';
import {
    config,
    ERROR_DEFAULT_TEXT,
    ERROR_OVERLAPPING,
    ERROR_PASSENGERS_NUMBER,
    FLIGHT_CUSTOM,
    FLIGHT_RECOMMENDED,
    WAY_ROUND
} from '../../../services/configService';

export const FLIGHTS_NAMESPACE = 'flights';

/**
 * List of vuex module getters
 */
export const FLIGHT_DATA_GETTER = 'getFlightData';
export const FLIGHT_SEARCH_MESSAGES_GETTER = 'flightSearchMessages';
export const SELECTED_FLIGHT_GETTER = 'selectedFlight';
export const SELECTED_FLIGHT_TRAVELLERS_GETTER = 'selectedFlightTravellers';
export const TRAVELLERS_DATA_GETTER = 'getTravellersData';
export const AIRLINE_GETTER = 'getAirlineName';
export const AIRLINES_GETTER = 'airlinesGetter';
export const AIRPORT_NAME_GETTER = 'getAirportName';
export const AIRPORT_CITY_GETTER = 'getAirportCityName';
export const AIRPORTS_GETTER = 'airportsGetter';
export const SEARCH_MODE_GETTER = 'getSearchMode';
export const IS_RECOMMENDED_GETTER = 'isRecommended';
export const IS_CUSTOM_GETTER = 'isCustom';

/**
 * List of vuex module mutations
 */
export const SET_WAY_TYPE_MUTATION = 'setWayType';
export const FLEXIBLE_DATES_MUTATION = 'flexibleDatesMutation';
export const FLIGHT_SEARCH_MESSAGES_MUTATION = 'flightSearchMessagesMutation';
export const AIRLINES_MUTATION = 'airlinesMutation';
export const AIRPORTS_MUTATION = 'airportsMutation';
export const SET_FLIGHT_MUTATION = 'setFlight';
export const SET_FLIGHTS_MUTATION = 'setFlights';
export const TRAVELLERS_MUTATION = 'setTravellers';
export const TRAVELLERS_DATA_MUTATION = 'setTravellersData';
export const DEPARTURE_ORIGIN_MUTATION = 'setDepartureOrigin';
export const DEPARTURE_DESTINATION_MUTATION = 'setDepartureDestination';
export const RETURN_ORIGIN_MUTATION = 'setReturnOrigin';
export const RETURN_DESTINATION_MUTATION = 'setReturnDestination';
export const DEPARTURE_DATE_MUTATION = 'setDepartureDate';
export const RETURN_DATE_MUTATION = 'setReturnDate';
export const SET_SEARCH_MODE_MUTATION = 'setSearchMode';
export const SET_RECOMMENDED_SEARCH_MODE_MUTATION = 'setRecommendedSearchMode';
export const SET_CUSTOM_SEARCH_MODE_MUTATION = 'setCustomSearchMode';
export const SET_AIR_INCLUSIVE_MUTATION = 'setAirInclusiveMutation';

/**
 * List of vuex module actions
 */
export const SET_WAY_TYPE_ACTION = 'setWayType';
export const SEARCH_FLIGHTS_ACTION = 'searchFlights';
export const SELECT_FLIGHT_ACTION = 'selectFlight';
export const UNSELECT_FLIGHT_ACTION = 'unselectFlight';
export const CLEAR_SEARCH_RESULT_ACTION = 'clearSearchResult';
export const PURCHASE_FLIGHT_ACTION = 'purchaseFlight';
export const UPDATE_DATA_ACTION = 'updateData';
export const UPDATE_RECOMMENDED_ACTION = 'updateRecommendedData';
export const SEARCH_AIRLINES_ACTION = 'searchAirlines';
export const SEARCH_AIRPORTS_ACTION = 'searchAirports';

export default {
    namespaced: true,
    state: {
        departure: {
            origin: config.location.departure.origin ? config.location.departure.origin : '',
            destination: config.location.departure.destination ? config.location.departure.destination : '',
            date: config.dates.departureDate
        },
        return: {
            origin: config.location.return.origin ? config.location.return.origin : '',
            destination: config.location.return.destination ? config.location.return.destination : '',
            date: config.dates.returnDate
        },
        travellersData: [],
        travellers: {
            ADT: config.travellers.adt,
            CHD: config.travellers.chd,
            INF: config.travellers.inf
        },
        searchMode: config.recommendedFlights.length ? FLIGHT_RECOMMENDED : FLIGHT_CUSTOM,
        wayType: config.wayType,
        flexibleDates: false,
        airInclusive: false,
        flights: {
            selected: null,
            default: config.recommendedFlights,
            searched: [],
            messages: []
        },
        airlines: [],
        airports: []
    },
    getters: {
        /**
         * Return state of flight data
         *
         * @param {Object} state - Vuex state
         *
         * @return {Object}
         */
        [FLIGHT_DATA_GETTER]: state => {
            return {
                type: state.searchMode,
                airInclusive: state.airInclusive ? 1 : 0,
                flight: state.flights.selected === null ? [] : state.flights.selected
            };
        },
        /**
         * Return state of flight data
         *
         * @param {Object} state - Vuex state
         *
         * @return {Object}
         */
        [FLIGHT_DATA_GETTER]: state => {
            return {
                type: state.searchMode,
                airInclusive: state.airInclusive ? 1 : 0,
                flight: state.flights.selected === null ? [] : state.flights.selected
            };
        },
        /**
         * Return true if search flights in recommended mode
         *
         * @param {Object} state - Vuex state
         *
         * @return {boolean}
         */
        [SEARCH_MODE_GETTER]: state => state.searchMode,
        /**
         * Return true if is selected
         *
         * @param {Object} state - Vuex state
         *
         * @return {boolean}
         */
        valid: state => {
            if (state.airInclusive === false) {
                return true;
            }
            return state.flights.selected !== null;
        },
        /**
         * Return true if search flights in recommended mode
         *
         * @param {Object} state - Vuex state
         *
         * @return {boolean}
         */
        [IS_RECOMMENDED_GETTER]: state => state.searchMode === FLIGHT_RECOMMENDED,
        /**
         * Return true if search flights in custom mode
         *
         * @param {Object} state - Vuex state
         *
         * @return {boolean}
         */
        [IS_CUSTOM_GETTER]: state => state.searchMode === FLIGHT_CUSTOM,
        /**
         * Return state of airline name
         *
         * @param {Object} state - Vuex state
         * @param {Array} airlineCode - List of airlines codes
         *
         * @return {string}
         */
        [AIRLINE_GETTER]: state => airlineCode => {
            const airline = state.airlines.find(airport => airport.code === airlineCode);
            return airline ? (airline.name === null ? airlineCode : airline.name) : airlineCode;
        },
        /**
         * Return state of airlines details
         *
         * @param {Object} state - Vuex state
         *
         * @return {Array}
         */
        [AIRLINES_GETTER]: state => state.airlines,
        /**
         * Return state of airport name
         *
         * @param {Object} state - Vuex state
         * @param {Array} airportCode - Airport IATA code
         *
         * @return {string}
         */
        [AIRPORT_NAME_GETTER]: state => airportCode => {
            const airport = state.airports.find(airport => airport.code === airportCode);
            return airport ? (airport.details ? airport.details.name : airportCode) : airportCode;
        },
        /**
         * Return state of airport city name
         *
         * @param {Object} state - Vuex state
         * @param {Array} airportCode - Airport IATA code
         *
         * @return {string}
         */
        [AIRPORT_CITY_GETTER]: state => airportCode => {
            const airport = state.airports.find(airport => airport.code === airportCode);
            return airport ? (airport.details ? airport.details.city : airportCode) : airportCode;
        },
        /**
         * Return state of airports details
         *
         * @param {Object} state - Vuex state
         *
         * @return {Array}
         */
        [AIRPORTS_GETTER]: state => state.airports,
        /**
         * Return state of selected flight
         *
         * @param {Object} state - Vuex state
         *
         * @return {Object}
         */
        [SELECTED_FLIGHT_GETTER]: state => state.flights.selected,
        /**
         * Return state of selected flight travellers
         *
         * @param {Object} state - Vuex state
         *
         * @return {Array}
         */
        [SELECTED_FLIGHT_TRAVELLERS_GETTER]: state => state.flights.selected ? state.flights.selected.travellers : [],
        /**
         * Return state of travellers data
         *
         * @param {Object} state - Vuex state
         *
         * @return {Array}
         */
        [TRAVELLERS_DATA_GETTER]: state => state.travellersData,
        /**
         * Return state of flight search messages
         *
         * @param {Object} state - Vuex state
         *
         * @return {Array}
         */
        [FLIGHT_SEARCH_MESSAGES_GETTER]: state => state.flights.messages
    },
    actions: {
        /**
         * Set way type
         *
         * @param {Object} context - Vuex actions context
         * @param {string} wayType
         *
         * @return {void}
         */
        [SET_WAY_TYPE_ACTION](context, wayType) {
            context.commit(SET_WAY_TYPE_ACTION, wayType);
            context.dispatch(SEARCH_FLIGHTS_ACTION);
        },
        /**
         * Search flights
         *
         * @param {Object} context - Vuex actions context
         *
         * @return {bool}
         */
        async [SEARCH_FLIGHTS_ACTION](context) {
            context.commit(FLIGHT_SEARCH_MESSAGES_MUTATION, []);
            if (!context.state.departure.date
                || (context.state.departure.origin.length !== 3)
                || (context.state.departure.destination.length !== 3)) {
                return false;
            }
            if (!context.state.travellers.ADT) {
                context.commit(FLIGHT_SEARCH_MESSAGES_MUTATION, [{
                    text: config.errorMessageText(ERROR_PASSENGERS_NUMBER)
                }]);
                return false;
            }
            if (context.state.departure.origin.toUpperCase() === context.state.departure.destination.toUpperCase()) {
                context.commit(FLIGHT_SEARCH_MESSAGES_MUTATION, [{
                    text: config.errorMessageText(ERROR_OVERLAPPING)
                }]);
                context.dispatch(CLEAR_SEARCH_RESULT_ACTION);
                return false;
            }
            const data = {
                flexibleDates: context.state.flexibleDates,
                wayType: context.state.wayType,
                travellers: context.state.travellers,
                departure: context.state.departure
            };
            if (context.state.wayType === WAY_ROUND) {
                if (!context.state.return.date) {
                    return false;
                }
                data.return = {
                    origin: context.state.return.origin ? context.state.return.origin : context.state.departure.destination,
                    destination: context.state.return.destination ? context.state.return.destination : context.state.departure.origin,
                    date: context.state.return.date
                };
                if (data.return.origin.toUpperCase() === data.return.destination.toUpperCase()) {
                    context.commit(FLIGHT_SEARCH_MESSAGES_MUTATION, [{
                        text: config.errorMessageText(ERROR_OVERLAPPING)
                    }]);
                    context.dispatch(CLEAR_SEARCH_RESULT_ACTION);
                    return false;
                }
            }
            try {
                const flights = await api.search(data);
                context.commit(TRAVELLERS_DATA_MUTATION, []);
                context.dispatch(UNSELECT_FLIGHT_ACTION);
                context.commit(SET_FLIGHTS_MUTATION, flights);
                if (flights.length) {
                    context.dispatch(UPDATE_DATA_ACTION, flights);
                }
            } catch (exception) {
                context.commit(
                    FLIGHT_SEARCH_MESSAGES_MUTATION,
                    exception.messages ? exception.messages : (exception.response ? exception.response.data.messages : [{text: ERROR_DEFAULT_TEXT}])
                );
                context.dispatch(CLEAR_SEARCH_RESULT_ACTION);
                return false;
            }
            return true;
        },
        /**
         * Select single flight
         *
         * @param {Object} context - Vuex actions context
         * @param {Array} flight - Single flight
         *
         * @return {void}
         */
        [SELECT_FLIGHT_ACTION](context, flight) {
            flight.travellersCount = 0;
            flight.travellers.forEach(traveller => {
                flight[traveller.type.toLowerCase()] = traveller;
                flight.travellersCount += parseInt(traveller.count);
            });
            flight.itineraries.forEach(itinerary => {
                itinerary.departure.locationName = context.getters[AIRPORT_NAME_GETTER](itinerary.departure.location);
                itinerary.departure.locationCityName = context.getters[AIRPORT_CITY_GETTER](itinerary.departure.location);
                itinerary.arrival.locationName = context.getters[AIRPORT_NAME_GETTER](itinerary.arrival.location);
                itinerary.arrival.locationCityName = context.getters[AIRPORT_CITY_GETTER](itinerary.arrival.location);
                if (itinerary.layover) {
                    itinerary.layover.locations.forEach(locations => {
                        if (locations.location) {
                            locations.locationName = context.getters[AIRPORT_NAME_GETTER](locations.location);
                            locations.locationCityName = context.getters[AIRPORT_CITY_GETTER](locations.location);
                        }
                    });
                }
                if (itinerary.name) {
                    itinerary.name = context.getters[AIRLINE_GETTER](itinerary.name);
                }
                itinerary.segments.forEach(segment => {
                    segment.carrierName = context.getters[AIRLINE_GETTER](segment.carrier);
                    segment.departure.locationName = context.getters[AIRPORT_NAME_GETTER](segment.departure.location);
                    segment.departure.locationCityName = context.getters[AIRPORT_CITY_GETTER](segment.departure.location);
                    segment.arrival.locationName = context.getters[AIRPORT_NAME_GETTER](segment.arrival.location);
                    segment.arrival.locationCityName = context.getters[AIRPORT_CITY_GETTER](segment.arrival.location);
                });
            });
            context.commit(SET_FLIGHT_MUTATION, flight);
        },
        /**
         * Unselect single flight
         *
         * @param {Object} context - Vuex actions context
         *
         * @return {void}
         */
        [UNSELECT_FLIGHT_ACTION](context) {
            context.commit(SET_FLIGHT_MUTATION, null);
        },
        /**
         * Clear search result
         *
         * @param {Object} context - Vuex actions context
         *
         * @return {void}
         */
        [CLEAR_SEARCH_RESULT_ACTION](context) {
            context.commit(SET_FLIGHTS_MUTATION, []);
        },
        /**
         * Unselect single flight
         *
         * @param {Object} context - Vuex actions context
         *
         * @return {void}
         */
        [UNSELECT_FLIGHT_ACTION](context) {
            context.commit(SET_FLIGHT_MUTATION, null);
        },
        /**
         * Purchase flight
         *
         * @param {Object} context - Vuex actions context
         * @param {Object} card
         *
         * @return {void}
         */
        async [PURCHASE_FLIGHT_ACTION](context, card) {
            const result = await api.purchaseFlight(card, context.state.travellersData, context.state.flights.selected);
            if (result && result.link) {
                location.href = result.link;
            }
        },
        /**
         * Update recommended flights data
         *
         * @param {Object} context - Vuex actions context
         *
         * @return {void}
         */
        [UPDATE_RECOMMENDED_ACTION](context) {
            context.dispatch(UPDATE_DATA_ACTION, context.state.flights.default);
        },
        /**
         * Update flights data
         *
         * @param {Object} context - Vuex actions context
         * @param {Array} flights - List of airlines codes
         *
         * @return {void}
         */
        [UPDATE_DATA_ACTION](context, flights) {
            const airlines = [];
            const airports = [];
            flights.forEach(flight => {
                flight.itineraries.forEach(itinerary => {
                    if (!airports.includes(itinerary.departure.location)) {
                        airports.push(itinerary.departure.location);
                    }
                    if (!airports.includes(itinerary.arrival.location)) {
                        airports.push(itinerary.arrival.location);
                    }
                    if (itinerary.layover) {
                        itinerary.layover.locations.forEach(locations => {
                            if (locations.location && !airports.includes(locations.location)) {
                                airports.push(locations.location);
                            }
                        });
                    }
                    if (itinerary.name) {
                        if (!airlines.includes(itinerary.name)) {
                            airlines.push(itinerary.name);
                        }
                    }
                    itinerary.segments.forEach(segment => {
                        if (!airlines.includes(segment.carrier)) {
                            airlines.push(segment.carrier);
                        }
                    });
                });
            });
            context.dispatch(SEARCH_AIRLINES_ACTION, airlines);
            context.dispatch(SEARCH_AIRPORTS_ACTION, airports);
        },
        /**
         * Search airlines details
         *
         * @param {Object} context - Action context
         * @param {Array} airlines - List of airlines codes
         *
         * @return {void}
         */
        async [SEARCH_AIRLINES_ACTION](context, airlines) {
            airlines = airlines.filter(airlineCode => !context.getters[AIRLINES_GETTER].find(searchedAirlines => searchedAirlines.code === airlineCode));
            if (airlines.length) {
                const airlinesDetails = await api.getAirlinesDetails(airlines);
                context.commit(AIRLINES_MUTATION, airlines.map(airlineCode => {
                    const airline = {
                        code: airlineCode
                    };
                    const airlineDetails = airlinesDetails.length ? airlinesDetails.find(airlineDetails => airlineDetails.iata_code === airlineCode) : null;
                    airline.name = airlineDetails ? airlineDetails.name : null;
                    return airline;
                }));
            }
        },
        /**
         * Search airports details
         *
         * @param {Object} context - Action context
         * @param {Array} airports - List of airports codes
         *
         * @return {array}
         */
        async [SEARCH_AIRPORTS_ACTION](context, airports) {
            airports = airports.filter(airportCode => !context.getters[AIRPORTS_GETTER].find(searchedAirports => searchedAirports.code === airportCode));
            if (airports.length) {
                const airportsDetails = await api.getAirportsDetails(airports);
                const preparedAirportsDetails = airports.map(airportCode => {
                    const airportDetails = airportsDetails.length ? airportsDetails.find(airportDetails => airportDetails.iata_code === airportCode) : null;
                    return {
                        code: airportCode,
                        details: airportDetails ? airportDetails : null
                    };
                });
                context.commit(AIRPORTS_MUTATION, preparedAirportsDetails);
                return preparedAirportsDetails;
            }
        }
    },
    mutations: {
        /**
         * Save state of search mode
         *
         * @param {Object} state - Vuex state
         * @param {string} searchMode
         *
         * @return {void}
         */
        [SET_SEARCH_MODE_MUTATION](state, searchMode) {
            state.searchMode = searchMode;
        },
        /**
         * Set recommended search mode to search mode state
         *
         * @param {Object} state - Vuex state
         *
         * @return {void}
         */
        [SET_RECOMMENDED_SEARCH_MODE_MUTATION](state) {
            state.searchMode = FLIGHT_RECOMMENDED;
        },
        /**
         * Set custom search mode to search mode state
         *
         * @param {Object} state - Vuex state
         *
         * @return {void}
         */
        [SET_CUSTOM_SEARCH_MODE_MUTATION](state) {
            state.searchMode = FLIGHT_CUSTOM;
        },
        /**
         * Save state of travellers
         *
         * @param {Object} state - Vuex state
         * @param {Array} travellers
         *
         * @return {void}
         */
        [TRAVELLERS_MUTATION](state, travellers) {
            state.travellers = travellers;
        },
        /**
         * Save state of travellers data
         *
         * @param {Object} state - Vuex state
         * @param {Array} travellersData
         *
         * @return {void}
         */
        [TRAVELLERS_DATA_MUTATION](state, travellersData) {
            state.travellersData = travellersData;
        },
        /**
         * Save state of departure origin
         *
         * @param {Object} state - Vuex state
         * @param {Array} origin
         *
         * @return {void}
         */
        [DEPARTURE_ORIGIN_MUTATION](state, origin) {
            state.departure.origin = origin;
        },
        /**
         * Save state of departure destination
         *
         * @param {Object} state - Vuex state
         * @param {Array} destination
         *
         * @return {void}
         */
        [DEPARTURE_DESTINATION_MUTATION](state, destination) {
            state.departure.destination = destination;
        },
        /**
         * Save state of return origin
         *
         * @param {Object} state - Vuex state
         * @param {Array} origin
         *
         * @return {void}
         */
        [RETURN_ORIGIN_MUTATION](state, origin) {
            state.return.origin = origin;
        },
        /**
         * Save state of return destination
         *
         * @param {Object} state - Vuex state
         * @param {Array} destination
         *
         * @return {void}
         */
        [RETURN_DESTINATION_MUTATION](state, destination) {
            state.return.destination = destination;
        },
        /**
         * Save state of departure date
         *
         * @param {Object} state - Vuex state
         * @param {Array} departureDate
         *
         * @return {void}
         */
        [DEPARTURE_DATE_MUTATION](state, departureDate) {
            state.departure.date = departureDate;
        },
        /**
         * Save state of return date
         *
         * @param {Object} state - Vuex state
         * @param {Array} returnDate
         *
         * @return {void}
         */
        [RETURN_DATE_MUTATION](state, returnDate) {
            state.return.date = returnDate;
        },
        /**
         * Save state of search wau type
         *
         * @param {Object} state - Vuex state
         * @param {Array} wayType
         *
         * @return {void}
         */
        [SET_WAY_TYPE_MUTATION](state, wayType) {
            state.wayType = wayType;
        },
        /**
         * Save state of flexible dates
         *
         * @param {Object} state - Vuex state
         * @param {boolean} flexibleDates
         *
         * @return {void}
         */
        [FLEXIBLE_DATES_MUTATION](state, flexibleDates) {
            state.flexibleDates = flexibleDates;
        },
        /**
         * Save state of selected flight
         *
         * @param {Object} state - Vuex state
         * @param {Array} flight - Selected flight
         *
         * @return {void}
         */
        [SET_FLIGHT_MUTATION](state, flight) {
            state.flights.selected = flight;
        },
        /**
         * Save state of searched flights
         *
         * @param {Object} state - Vuex state
         * @param {Array} flights
         *
         * @return {void}
         */
        [SET_FLIGHTS_MUTATION](state, flights) {
            state.flights.searched = flights;
        },
        /**
         * Save state of flights search messages
         *
         * @param {Object} state - Vuex state
         * @param {Array} messages - Flight search messages
         *
         * @return {void}
         */
        [FLIGHT_SEARCH_MESSAGES_MUTATION](state, messages) {
            state.flights.messages = messages;
        },
        /**
         * Save airlines details state
         *
         * @param {Object} state - Vuex state
         * @param {Array} airlines - Airlines details
         *
         * @return {void}
         */
        [AIRLINES_MUTATION](state, airlines) {
            airlines.forEach(airline => {
                state.airlines.push(airline);
            });
        },
        /**
         * Save airports details state
         *
         * @param {Object} state - Vuex state
         * @param {Array} airports - Airports details
         *
         * @return {void}
         */
        [AIRPORTS_MUTATION](state, airports) {
            airports.forEach(airport => {
                state.airports.push(airport);
            });
        },
        /**
         * Save air inclusive state
         *
         * @param {Object} state - Vuex state
         * @param {Array} airInclusive
         *
         * @return {void}
         */
        [SET_AIR_INCLUSIVE_MUTATION](state, airInclusive) {
            state.airInclusive = airInclusive;
        }
    }
};